<template>
  <div ref="contentOrgEl" class="content-organization">
    <search-header
      title="Notifications"
      active="filters"
      filter-url="/my/notifications"
      :show-tags="true"
      @tag-update="tagUpdate"
      @clear-all="clearAll"
    />

    <div ref="contentResultsEl" class="content-type-options">
      <search-filters
        :loading="page.loading"
        :show-keywords="false"
        :show-tags="false"
        :entity-type="entityType"
        @filter-update="filterUpdate"
      >
        <notification-filters @filter-update="filterUpdate" />
      </search-filters>

      <div class="content-results">
        <!-- error state, modal will also show -->
        <callout v-if="errorMsg" type="error">
          <div>{{ errorMsg }}</div>
        </callout>

        <!-- result top bar -->
        <div class="results-details">
          <search-sort :total="searchResults.total" :sortable="false" @sort-update="sortUpdate" />
          <search-result-actions
            :active-expander="activeExpander"
            :accessibility-expander-id="accessibilityExpanderId"
            @expander-click="toggleActiveExpander"
            @mark-all-read="markAllRead"
            @delete-all="openConfirmModal"
          >
            <div v-if="!!userAuthStore.user.notificationUnreadCount" class="action">
              <icon-button
                type="button"
                icon="mark_email_read"
                label="Mark All Read"
                :submitting="markAllReadLoading"
                classes="add"
                :icons-outlined="false"
                @click-action="markAllRead"
              />
            </div>
            <div v-if="!!userAuthStore.user.notificationTotal" class="action">
              <icon-button
                type="button"
                icon="delete"
                label="Delete All"
                :submitting="deleteAllLoading"
                classes="surprise"
                :icons-outlined="false"
                @click-action="openConfirmModal"
              />
            </div>
          </search-result-actions>
        </div>

        <!-- results -->
        <search-result-notifications
          v-if="!errorMsg && (searchResults.loading || searchResults.total > 0)"
          :loading="searchResults.loading && !loadingMore"
          :per-page-total="10"
        >
          <template #notification>
            <notification
              v-for="notification in searchResultsData"
              :key="notification.id"
              class="fb-notification"
              :notification="notification"
              @deleted="deleteNotification"
              @updating="updateNotification"
              @updated="updateNotificationAndFacets"
            />
          </template>
          <template #infinity-scroller>
            <div id="infinity-scroller" ref="infinityScroller" />
          </template>
          <template #spinner-loader>
            <spinner-loader v-if="loadingMore" />
          </template>
        </search-result-notifications>
        <search-no-results v-else title="No Notifications Found" @clear-all="clearAll" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Note: notificationTotal is being passed to the front-end via blade, if we introduce any
 * router-links we will need to refactor to use pinia store to access the notification count
 */

import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import SearchHeader from '@/components/search/search-header.vue'
import SearchFilters from '@/components/search/search-filters.vue'
import SearchSort from '@/components/search/search-sort.vue'
import SearchResultActions from '@/components/search/search-result-actions.vue'
import SearchResultNotifications from '@/components/notifications/search-result-notifications.vue'
import { useUserAuthStore } from '@/stores/user-auth'
import { useSearchStore } from '@/stores/search'
import { useModalsStore } from '@/stores/modals'
import { useSearchRoute } from '@/composables/search-route'
import type { UserNotification } from '@/types/model-types'
import { removeArraySyntax } from '@/utils/helpers'
import { fbApi, isAbortError, objToSearchParams } from '@/utils/http-api'
import type { InitCheckboxFilter, UserNotificationFacets } from '@/types/search-types'
import Callout from '@/components/notifications/callout.vue'
import NotificationFilters from '@/components/notifications/notification-filters.vue'
import Notification from '@/components/notifications/notification.vue'
import SpinnerLoader from '@/components/loaders/spinner-loader.vue'
import IconButton from '@/components/buttons/icon-button.vue'
import SearchNoResults from '@/components/search/search-no-results.vue'

/** TYPES **/
type ActiveExpander = 'surprise' | 'categories' | ''

/** STATE **/
const route = useRoute()
const userAuthStore = useUserAuthStore()
const searchStore = useSearchStore()
const modalsStore = useModalsStore()
const {
  contentResultsEl,
  contentOrgEl,
  scrollAndFocus,
  tagUpdate,
  filterUpdate,
  sortUpdate,
  clearAll,
} = useSearchRoute()

const page = ref<{
  loading: boolean
  error: string
}>({
  loading: true, // default the page to loading
  error: '',
})

const searchResults = ref<{
  loading: boolean
  error: string
  total: number
  perPage: number
  cursorName: string
  hasMorePages: boolean
  hasPages: boolean
  nextPageUrl: string
  nextPageUrlHash: string
}>({
  loading: false,
  error: '',
  total: 0,
  perPage: 0,
  cursorName: '',
  hasMorePages: false,
  hasPages: false,
  nextPageUrl: '',
  nextPageUrlHash: '',
})

const cursorHash = ref<string | null>(null)
const infinityScroller = ref<HTMLElement | null>(null)
const markAllReadLoading = ref<boolean>(false)
const deleteAllLoading = ref<boolean>(false)

const searchResultsData = ref<UserNotification[]>([])

const activeExpander = ref<ActiveExpander>('')

/** NON REACTIVE **/
let fetchAbort: AbortController | null = null

const modalId = 'delete-all-notifications-modal'

const accessibilityExpanderId = 'results-expander-control'
const entityType = 'notification'

const checkboxFilters: Record<string, InitCheckboxFilter> = {
  notificationStatus: {
    label: 'Inbox',
    name: 'notificationStatus',
    items: [
      { label: 'Unread', value: 'unread' },
      { label: 'Read', value: 'read' },
    ],
  },
  type: {
    label: 'Notification Type',
    name: 'type',
    items: [
      { label: 'Achievement', value: 'achievement_level' },
      { label: 'Comment', value: 'comment' },
      { label: 'Gift Code', value: 'coupon' },
      { label: 'System', value: '' },
      { label: 'Fitness Blender', value: 'admin_notification' },
    ],
  },
}

/** COMPUTED **/
const errorMsg = computed(() => page.value.error || searchResults.value.error)
const loadingMore = computed<boolean>(
  () => searchResults.value.loading && Boolean(cursorHash.value),
)

const userNotificationFacets = computed<UserNotificationFacets>(() => {
  console.log('userNotificationFacets', userAuthStore.user.notificationUnreadCount)
  const notificationTotal = userAuthStore.user.notificationTotal || 0

  return {
    notificationStatus: {
      unread: userAuthStore.user.notificationUnreadCount,
      read: notificationTotal - userAuthStore.user.notificationUnreadCount,
    },
    type: {
      achievement: 0,
      comment: 0,
      coupon: 0,
      system: 0,
      admin_notification: 0,
    },
  }
})

/** WATCHERS **/
watch(
  () => route.query,
  () => {
    // resync on every query string change
    syncUrlToState({ init: false })

    // refresh data
    fetchNotifications({ clearCursor: true })
  },
)

watch(
  () => modalsStore.simpleModal.action,
  (newAction, prevAction) => {
    if (
      modalsStore.simpleModal.id === modalId &&
      newAction !== prevAction &&
      newAction === 'confirm' &&
      !deleteAllLoading.value
    ) {
      deleteAllNotifications()
    }
  },
)

/** LIFECYCLE **/
onBeforeMount(async () => {
  // clear everything
  searchStore.$reset()

  syncUrlToState({ init: true })

  // do not wait for this to finish before moving on
  fetchNotifications({ clearCursor: true })
})

onMounted(() => {
  // add event listener to show / hide based on scroll position
  document.addEventListener('scroll', infinityScroll, {
    passive: true,
  })
})

// cancel fetch request, especially if going to browse view as it will
// as it will use the search store
onBeforeUnmount(() => {
  if (fetchAbort) {
    fetchAbort.abort()
  }

  // clean up
  document.removeEventListener('scroll', infinityScroll)
})

/** METHODS **/

/**
 * Update pinia based on url query params as the source of truth.
 * Clear out values and reset them based on the query params
 */
function syncUrlToState({ init }: { init: boolean }) {
  // removes [] and forces array for items with [] in var names
  let queryParams = removeArraySyntax(route.query)

  // filters
  searchStore.setCheckboxFilters({
    checkboxFilters,
    queryStringFilters: queryParams,
    checkboxUrlParameterNames: searchStore.createIncludeExcludeParamMap(
      {
        notificationStatus: 'notificationStatus',
        type: 'type',
      },
      userAuthStore.features.VIDEO_FILTERS,
    ),
    init,
  })

  // rebuild tags last, after all pinia state is loaded
  searchStore.setInitialTags()
}

async function fetchNotifications({ clearCursor = true }: { clearCursor: boolean }) {
  // clear any error
  searchResults.value.error = ''
  searchResults.value.loading = true

  // format GET query params
  let params = route.query || {}
  params = removeArraySyntax(params)

  if (clearCursor) {
    cursorHash.value = null
  }

  if (cursorHash.value) {
    // nextPageUrlHash is manipulated to retrieve the next cursor hash for url
    // see ApiOutputTrait::uiCursorPagination
    params['cursor'] = cursorHash.value
  }

  try {
    // cancel any previous fetch
    if (fetchAbort) {
      fetchAbort.abort()
    }

    // re-init abort controller
    fetchAbort = new AbortController()

    const res = await fbApi.get('/notifications', {
      searchParams: objToSearchParams(params),
      signal: fetchAbort.signal,
    })

    if (!res || !res.body || !res.body.data) {
      throw new Error(`Unable to fetch notifications, please refresh the page and try again`)
    }

    const {
      notifications,
    }: {
      notifications: UserNotification[]
    } = res.body.data

    let loadedNotifications = null
    searchResults.value = res.body.data

    // on initial load, first page, just set results
    if (!cursorHash.value) {
      searchResultsData.value = notifications
    } else {
      // if loading more notifications, concat new notifications to end of current set
      loadedNotifications = [...searchResultsData.value]

      searchResultsData.value = loadedNotifications.concat(notifications)
    }

    // update cursorHash
    cursorHash.value = res.body.data.nextPageUrlHash

    // pinia facets
    searchStore.setAllowEmptyFacets(true)
    searchStore.updateFacets(userNotificationFacets.value)
  } catch (err) {
    // skip if Aborted
    if (isAbortError(err)) {
      console.warn('fetchCustomWorkouts aborted')
      return
    }

    // oops model will show
    console.error(err)
    searchResults.value.error = `Unable to fetch notifications, please refresh the page and try again`
  } finally {
    searchResults.value.loading = false
    // clear any error and set both loading stats so skeleton cards show
    page.value.error = ''
    page.value.loading = false
  }
}

function toggleActiveExpander(expander: ActiveExpander) {
  if (activeExpander.value === expander) {
    activeExpander.value = ''
    return
  }

  activeExpander.value = expander
}

// Child Notification component handles the API call and emits to allow this
// parent to visually manipulate the loaded results
function deleteNotification(notification: UserNotification) {
  const notificationIndex = searchResultsData.value.indexOf(notification)
  searchResultsData.value.splice(notificationIndex, 1)
  searchResults.value.total -= 1

  // update count
  userAuthStore.updateUser({
    ...userAuthStore.user,
    notificationTotal: userAuthStore.user.notificationTotal! - 1,
  })

  // update facets
  searchStore.updateFacets(userNotificationFacets.value)
}

// powers both read and unread calls
// Child Notification component handles the API call and emits to allow this
// component to visually manipulate the loaded results
function updateNotification({ id, isRead }: { id: number; isRead: number }) {
  const notification = searchResultsData.value.find((item: UserNotification) => item.id === id)

  if (!notification) {
    return
  }

  notification.isRead = isRead
}

function updateNotificationAndFacets({ id, isRead }: { id: number; isRead: number }) {
  updateNotification({ id, isRead })
  searchStore.updateFacets(userNotificationFacets.value)
}

async function markAllRead() {
  if (markAllReadLoading.value) {
    return
  }

  markAllReadLoading.value = true
  const currUnreadCount = userAuthStore.user.notificationUnreadCount
  const url = '/notifications/read-all'

  try {
    const res = await fbApi.put(url)

    if (!res || !res.body) {
      throw new Error(`Unable to update notifications, please try again`)
    }

    markAllReadLoading.value = false
    searchResults.value.error = ''
    searchResultsData.value.forEach((notification: UserNotification) => (notification.isRead = 1))
    userAuthStore.updateUser({ ...userAuthStore.user, notificationUnreadCount: 0 })

    // update facets
    searchStore.updateFacets(userNotificationFacets.value)
  } catch (err) {
    markAllReadLoading.value = false
    searchResults.value.error = `Unable to update notifications, please try again`
    userAuthStore.updateUser({ ...userAuthStore.user, notificationUnreadCount: currUnreadCount })
    scrollAndFocus({ scrollTo: contentOrgEl })
  }
}

function openConfirmModal() {
  modalsStore.openSimpleModal({
    type: 'CONFIRM',
    id: modalId,
    props: {
      heading: 'Delete All Notifications',
      subheading: 'Are you sure you want to delete all notifications?',
      buttonText: 'Delete All',
    },
  })
}

async function deleteAllNotifications() {
  if (deleteAllLoading.value) {
    return
  }

  deleteAllLoading.value = true
  searchResults.value.error = ''

  const currUnreadCount = userAuthStore.user.notificationUnreadCount
  const url = '/notifications/delete-all'

  try {
    const res = await fbApi.delete(url)

    if (!res || !res.body) {
      throw new Error(`Unable to delete notifications, please try again`)
    }

    searchResultsData.value = []
    userAuthStore.updateUser({
      ...userAuthStore.user,
      notificationUnreadCount: 0,
      notificationTotal: 0,
    })
    searchResults.value.total = 0
  } catch (err) {
    searchResults.value.error = `Unable to delete notifications, please try again`
    userAuthStore.updateUser({ ...userAuthStore.user, notificationUnreadCount: currUnreadCount })
    scrollAndFocus({ scrollTo: contentOrgEl })
  } finally {
    deleteAllLoading.value = false

    // update facets
    searchStore.updateFacets(userNotificationFacets.value)
  }
}

function loadMore() {
  fetchNotifications({ clearCursor: false })
}

function infinityScroll() {
  // short circuit if already loading
  if (searchResults.value.loading || !searchResults.value.hasMorePages) {
    return
  }

  // look for empty div above load more
  const target = infinityScroller.value

  // in case of no results, ref will not exist
  if (!target) {
    return
  }

  const targetLocation = target.getBoundingClientRect()
  let inView = false

  // checking both window and documentElement height/width
  // Modern browsers tend to use window.innerHeight, however some older
  // browser versions utilize document.documentElement and vary in calculation methods
  // between the two. Checking both here to cover most bases
  inView =
    targetLocation.top >= 0 &&
    targetLocation.left >= 0 &&
    targetLocation.bottom <=
      (window.innerHeight || (document.documentElement && document.documentElement.clientHeight)) &&
    targetLocation.right <=
      (window.innerWidth || (document.documentElement && document.documentElement.clientWidth))

  if (inView && searchResults.value.hasMorePages) {
    loadMore()
  }
}
</script>
